import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Target, Users, DollarSign, BarChart3 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

export default function ManagementDashboard() {
  const { API } = useContext(AuthContext);
  const [managementData, setManagementData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [customerAnalysis, setCustomerAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCustomer !== 'all') {
      loadCustomerAnalysis(selectedCustomer);
    } else {
      setCustomerAnalysis(null);
    }
  }, [selectedCustomer]);

  const loadData = async () => {
    try {
      // Load independently so one failure doesn't break the other
      const managementRes = await axios.get(`${API}/reports/management-dashboard`).catch(() => ({ data: null }));
      const customersRes = await axios.get(`${API}/customers`).catch(() => ({ data: [] }));

      setManagementData(managementRes.data);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerAnalysis = async (customerId) => {
    try {
      const response = await axios.get(`${API}/reports/customer-analysis/${customerId}`);
      setCustomerAnalysis(response.data);
    } catch (error) {
      console.error('Error loading customer analysis:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  const revenueChartData = managementData?.revenue_by_month
    ? Object.entries(managementData.revenue_by_month).map(([month, value]) => ({
      month,
      ingresos: value,
      gastos: managementData.expenses_by_month[month] || 0
    }))
    : [];

  const expensesChartData = managementData?.top_expenses || [];

  const COLORS = ['#1a4d2e', '#2d7a4a', '#40a766', '#66c285', '#8cd9a4'];

  const customerRevenueData = customerAnalysis?.monthly_revenue
    ? Object.entries(customerAnalysis.monthly_revenue).map(([month, value]) => ({
      month,
      ingresos: value
    }))
    : [];

  return (
    <div className="p-8 space-y-8" data-testid="management-dashboard-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Dashboard de Gestión</h1>
          <p className="text-muted-foreground">Análisis estratégico y KPIs de negocio</p>
        </div>
        <div className="w-72">
          <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
            <SelectTrigger data-testid="customer-selector">
              <SelectValue placeholder="Seleccionar cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los clientes</SelectItem>
              {Array.isArray(customers) && customers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedCustomer === 'all' ? (
        <>
          {/* Vista General */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Ingresos Totales</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{formatCurrency(managementData?.total_revenue || 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">Período completo</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Gastos Totales</CardTitle>
                <TrendingDown className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{formatCurrency(managementData?.total_expenses || 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">Período completo</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Margen Neto</CardTitle>
                <Target className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{managementData?.net_margin?.toFixed(1) || 0}%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {managementData?.net_margin >= 0 ? 'Rentable' : 'Pérdida'}
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Clientes Activos</CardTitle>
                <Users className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{managementData?.total_customers || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Promedio: {formatCurrency(managementData?.average_revenue_per_customer || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Evolución de Ingresos y Gastos</CardTitle>
                <CardDescription>Análisis mensual</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="ingresos" stroke="#1a4d2e" strokeWidth={2} name="Ingresos" />
                    <Line type="monotone" dataKey="gastos" stroke="#dc2626" strokeWidth={2} name="Gastos" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Gastos</CardTitle>
                <CardDescription>Categorías de mayor impacto</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={expensesChartData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="amount" fill="#1a4d2e" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Análisis de Distribución */}
          <Card>
            <CardHeader>
              <CardTitle>Distribución de Gastos por Categoría</CardTitle>
              <CardDescription>Top 5 categorías</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expensesChartData.slice(0, 5)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${formatCurrency(entry.amount)}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {expensesChartData.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Vista por Cliente */}
          {customerAnalysis && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="hover:shadow-md transition-all">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Cliente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold">{customerAnalysis.customer.name}</div>
                    <p className="text-xs text-muted-foreground mt-1">RUT: {customerAnalysis.customer.rut}</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-all">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Ingresos Totales</CardTitle>
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono">{formatCurrency(customerAnalysis.total_revenue)}</div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-all">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Transacciones</CardTitle>
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{customerAnalysis.transaction_count}</div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-all">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Ticket Promedio</CardTitle>
                    <Target className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono">{formatCurrency(customerAnalysis.average_transaction)}</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Evolución de Ingresos - {customerAnalysis.customer.name}</CardTitle>
                  <CardDescription>Ingresos mensuales generados</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={customerRevenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="ingresos" fill="#1a4d2e" name="Ingresos" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Información del Cliente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Email:</p>
                      <p className="font-medium">{customerAnalysis.customer.email || 'No registrado'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Teléfono:</p>
                      <p className="font-medium">{customerAnalysis.customer.phone || 'No registrado'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Dirección:</p>
                      <p className="font-medium">{customerAnalysis.customer.address || 'No registrada'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Saldo:</p>
                      <p className="font-medium font-mono">{formatCurrency(customerAnalysis.customer.balance)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}